import { useTranslation } from "react-i18next";
import shared from "./shared.module.css";
import styles from "./RoundTransitionScreen.module.css";
import { Avatar } from "../../ui/Avatar";
import { DealerChip } from "../../ui/sk/DealerChip";
import { SkullGlyph } from "../../ui/sk/SkGlyphs";
import { displayPlayerName } from "../../../../shared/players";
import { useRequiredViewerId } from "../../../hooks/useRequiredViewerId";
import { useOwnedProfileIndex } from "../../../hooks/data/useOwnedProfileIndex";
import type { Player } from "../../../types/match";

type Standing = {
  player: Player;
  total: number;
  /** Score delta from the most recently completed round. */
  lastDelta: number;
};

type Props = {
  /** The round that just ended. */
  completedRound: number;
  /** The next round (1-based). */
  nextRound: number;
  /** Player who deals the next round. */
  nextDealer: Player;
  /** Standings sorted by total (leader first). */
  standings: Standing[];
  onContinue: () => void;
  /** Re-enter the result phase for the round that just ended (typo fix
   * affordance). */
  onEditLastRound?: () => void;
};

export function RoundTransitionScreen({
  completedRound,
  nextRound,
  nextDealer,
  standings,
  onContinue,
  onEditLastRound,
}: Props) {
  const { t } = useTranslation();
  const viewerId = useRequiredViewerId();
  const ownedIndex = useOwnedProfileIndex(viewerId);
  // One card per round number, no cap — round 10 deals 10 cards. The stack
  // is centred horizontally regardless of count.
  const cardCount = nextRound;
  const CARD_WIDTH = 60;
  const CARD_OVERLAP_STEP = 14;
  const stackSpan = (cardCount - 1) * CARD_OVERLAP_STEP + CARD_WIDTH;

  return (
    <div
      className={`${shared.screen} ${styles.body}`}
      data-testid="sk-transition"
    >
      <div className={styles.heading}>
        <div className={shared.caption}>
          {t("scoring.skullKing.transition.caption", { n: completedRound })}
        </div>
        <h1 className={styles.bigTitle}>
          {t("scoring.skullKing.transition.roundTitle", { n: nextRound })}
        </h1>
        <p className={styles.subtitle}>
          {t("scoring.skullKing.transition.cardsEach", {
            count: nextRound,
            name: displayPlayerName(nextDealer, ownedIndex),
          })}
        </p>
      </div>

      <div className={styles.dealerChipRow}>
        <DealerChip
          profile={nextDealer.profile}
          viewerId={viewerId}
          label={t("scoring.skullKing.transition.dealerChip", {
            name: displayPlayerName(nextDealer, ownedIndex),
          })}
        />
      </div>

      <div className={styles.cardStack} aria-hidden>
        {Array.from({ length: cardCount }).map((_, i) => {
          const isTop = i === cardCount - 1;
          // Each card's left edge is computed off the centre. The base
          // offset (-stackSpan/2) anchors the leftmost card so the whole
          // spread sits centred horizontally; each subsequent card steps
          // CARD_OVERLAP_STEP px to the right.
          return (
            <div
              key={i}
              className={styles.card}
              style={{
                left: `calc(50% - ${stackSpan / 2}px + ${i * CARD_OVERLAP_STEP}px)`,
                top: 8 + i * 1,
                transform: `rotate(${(i - (cardCount - 1) / 2) * 4}deg)`,
              }}
            >
              {isTop && (
                <SkullGlyph
                  size={44}
                  color="var(--color-ink-soft)"
                  crownColor="var(--sk-gold)"
                />
              )}
            </div>
          );
        })}
      </div>

      <div className={styles.standings} data-testid="sk-transition-standings">
        <div className={shared.caption}>
          {t("scoring.skullKing.transition.standingsTitle")}
        </div>
        {standings.map((row, i) => {
          const sign = row.lastDelta >= 0 ? "+" : "";
          const direction = row.lastDelta >= 0 ? styles.up : styles.down;
          const isLeader = i === 0;
          return (
            <div
              key={row.player.id}
              className={styles.standingsRow}
              data-testid={`sk-transition-standing-${i}`}
            >
              <span className={styles.standingsRank}>#{i + 1}</span>
              <Avatar
                profile={row.player.profile}
                viewerId={viewerId}
                size="md"
                winner={isLeader}
              />
              <span className={styles.standingsName}>
                {displayPlayerName(row.player, ownedIndex)}
              </span>
              <span className={`${styles.standingsDelta} ${direction}`}>
                {sign}
                {row.lastDelta}
              </span>
              <span className={styles.standingsTotal}>{row.total}</span>
            </div>
          );
        })}
      </div>

      <div
        style={{
          marginTop: "auto",
          display: "flex",
          flexDirection: "column",
          gap: 8,
        }}
      >
        {onEditLastRound && (
          <button
            type="button"
            className={shared.btnGhost}
            onClick={onEditLastRound}
            data-testid="sk-transition-edit-last"
            style={{ alignSelf: "center" }}
          >
            {t("scoring.skullKing.transition.editLastRoundCta", {
              n: completedRound,
            })}
          </button>
        )}
        <button
          type="button"
          className={shared.btnPrimary}
          onClick={onContinue}
          data-testid="sk-transition-continue"
        >
          {t("scoring.skullKing.transition.continueCta", { n: nextRound })}
        </button>
      </div>
    </div>
  );
}
